import { PlanLimits } from "../../../../../constants/subscription";
import { BuyerType } from "../../../../../constants/enums";
import { activeDays, DAY_KEYS, isTimeRangeValid } from "./schedule";

/**
 * Validation utilities for wizard steps
 *
 * Each validator accepts an optional i18next-style `t(key, defaultValue)`
 * translator so callers rendering these messages in the UI can localize
 * them (see Dashboard.Wizard.Steps.validation.* keys). When no translator
 * is supplied, the English default value is used as-is so these functions
 * remain plain and easy to unit test.
 */
const identityT = (_key, defaultValue) => defaultValue;

const BUSINESS_NAME_MAX = 200;

export const validateBusinessDetails = (formData, t = identityT) => {
  const newErrors = {};

  if (!formData.businessName?.trim()) {
    newErrors.businessName = t(
      "Dashboard.Wizard.Steps.validation.businessNameRequired",
      "Business name is required",
    );
  } else if (formData.businessName.trim().length > BUSINESS_NAME_MAX) {
    // OrganizationRequest's [StringLength]: past it the API refuses the save
    // with nothing on this step pointing at the field.
    newErrors.businessName = t(
      "Dashboard.Wizard.Steps.validation.resourceNameTooLong",
      `Name can be at most ${BUSINESS_NAME_MAX} characters`,
      { max: BUSINESS_NAME_MAX },
    );
  }

  // Only a company has a fiscal code. A private individual is invoiced B2C and
  // is never asked for one — requiring it here is what used to make signing up
  // impossible for them, since the API rejects an empty value outright.
  // Checked before the legal name because the step renders it first: the
  // insertion order is the order focusFirstError walks.
  if (
    Number(formData.buyerType) !== BuyerType.INDIVIDUAL &&
    !formData.registrationCode?.trim()
  ) {
    newErrors.registrationCode = t(
      "Dashboard.Wizard.Steps.validation.registrationCodeRequired",
      "Fiscal code is required",
    );
  }

  if (!formData.companyName?.trim()) {
    newErrors.companyName = t(
      "Dashboard.Wizard.Steps.validation.companyNameRequired",
      "Company name is required",
    );
  }

  // No rule for the phone number, on purpose. It is labelled "(optional)" on the
  // form and `OrganizationRequest.PhoneNumber` is nullable, so requiring it here
  // contradicted both — and because the phone input is hand-rolled around the
  // country-code select instead of going through `Field`, it never rendered its
  // error. The result was the one failure mode a form must not have: Continue
  // did nothing at all, with nothing on screen saying why.

  if (!formData.county?.trim()) {
    newErrors.county = t(
      "Dashboard.Wizard.Steps.validation.countyRequired",
      "County is required",
    );
  }

  if (!formData.city?.trim()) {
    newErrors.city = t(
      "Dashboard.Wizard.Steps.validation.cityRequired",
      "City is required",
    );
  }

  if (!formData.address?.trim()) {
    newErrors.address = t(
      "Dashboard.Wizard.Steps.validation.addressRequired",
      "Address is required",
    );
  }

  return newErrors;
};

/**
 * The DOM id of one resource's input, and the key its error is stored under.
 * One string for both on purpose: `focusFirstError` finds the field to scroll
 * to by looking the error key up as an element id, the same way step 2 does.
 */
export const resourceFieldId = (field, resourceId) =>
  `resource-${field}-${resourceId}`;

// ResourceRequest's [StringLength] limits.
const RESOURCE_NAME_MAX = 200;
const RESOURCE_EMAIL_MAX = 255;

// Stricter than the API's [EmailAddress], which only asks for a single "@"
// that is neither first nor last. "ana@gmail" and "ana @x.ro" get through
// there, and the invitation then bounces with nobody watching; here they are
// caught while the person who typed them is still looking at the field.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What the wizard sends as a resource's email: trimmed, and only for a Person. */
export const resourceEmailToSave = (resource) =>
  resource.type === "Person" ? resource.email?.trim() || null : null;

const resourceNameError = (rawName, t) => {
  const name = rawName ?? "";
  if (!name.trim()) {
    return t(
      "Dashboard.Wizard.Steps.validation.resourceNameRequired",
      "Name is required",
    );
  }
  if (name.length > RESOURCE_NAME_MAX) {
    return t(
      "Dashboard.Wizard.Steps.validation.resourceNameTooLong",
      `Name can be at most ${RESOURCE_NAME_MAX} characters`,
      { max: RESOURCE_NAME_MAX },
    );
  }
  return null;
};

const resourceEmailError = (t) =>
  t(
    "Dashboard.Wizard.Steps.validation.resourceEmailInvalid",
    "This email address is not valid. Use the form name@domain.com",
  );

/**
 * What is wrong with a resource's week, keyed the way the editor renders it:
 * no day at all, or a day that ends before it starts. Either one leaves the
 * resource with nothing a customer could book, and the API stores it anyway.
 */
const resourceScheduleErrors = (resource, t) => {
  const errors = {};

  if (activeDays(resource).length === 0) {
    errors[resourceFieldId("days", resource.id)] = t(
      "Dashboard.Wizard.Steps.validation.resourceDaysRequired",
      "Choose at least one day",
    );
  }

  const ranges = resource.useAdvancedSchedule
    ? DAY_KEYS.map((day) => resource.advancedSchedule?.[day]).filter(
        (day) => day?.enabled,
      )
    : [resource.workingHours];
  if (ranges.some((range) => !isTimeRangeValid(range?.start, range?.end))) {
    errors[resourceFieldId("hours", resource.id)] = t(
      "Dashboard.Wizard.Steps.validation.resourceHoursInvalid",
      "The end time must be after the start time",
    );
  }

  return errors;
};

export const validateResources = (resources, plan, t = identityT) => {
  const newErrors = {};

  // Per resource, in render order, so the first key is the topmost field.
  // Without these the API was the only check, and its 400 came back to a step
  // that rendered no error at all — Continue just stopped responding.
  (resources ?? []).forEach((resource) => {
    const nameError = resourceNameError(resource.name, t);
    if (nameError) {
      newErrors[resourceFieldId("name", resource.id)] = nameError;
    }

    // Required for a person: it is how they are invited to see their own
    // bookings — or, when it is the owner's own address, how the resource is
    // linked to the owner's account without an invitation (ResourceServices).
    const email = resourceEmailToSave(resource);
    if (resource.type === "Person" && !email) {
      newErrors[resourceFieldId("email", resource.id)] = t(
        "Dashboard.Wizard.Steps.validation.resourceEmailRequired",
        "Email is required",
      );
    } else if (
      email &&
      (email.length > RESOURCE_EMAIL_MAX || !EMAIL_PATTERN.test(email))
    ) {
      newErrors[resourceFieldId("email", resource.id)] = resourceEmailError(t);
    }

    Object.assign(newErrors, resourceScheduleErrors(resource, t));
  });

  if (!resources || resources.length === 0) {
    newErrors.resources = t(
      "Dashboard.Wizard.Steps.validation.resourcesRequired",
      "At least one resource is required",
    );
  } else {
    // Only check the plan's seat cap once we know there's at least one
    // resource — telling someone with zero resources they have "too many"
    // would just mask the more useful "add at least one" message above.
    const maxStaff = PlanLimits[Number(plan)]?.maxStaff;
    if (maxStaff != null && resources.length > maxStaff) {
      newErrors.resources = t(
        "Dashboard.Wizard.Steps.validation.resourcesOverPlanLimit",
        `Your plan allows up to ${maxStaff} team member(s), but you have ${resources.length}. Remove one or upgrade your plan.`,
        { max: maxStaff, count: resources.length },
      );
    }
  }

  return newErrors;
};

/**
 * Turn the API's refusal of one resource into marks on that resource's fields.
 *
 * `saveResources` stamps the resource it was saving onto the error as
 * `failedResource`. A 400 carries `errors` keyed by the C# property name
 * ("Email"), or by a JSON path ("$.email") when the body failed to bind.
 * Returns null when there is nothing field-shaped to point at, so the caller
 * falls back to its general message.
 */
export const resourceErrorsFromResponse = (error, t = identityT) => {
  const resource = error?.failedResource;
  const fields =
    error?.response?.status === 400 ? error.response.data?.errors : null;
  if (!resource || !fields) return null;

  const flagged = new Set(
    Object.keys(fields).map((key) => key.replace(/^\$\./, "").toLowerCase()),
  );

  const newErrors = {};
  if (flagged.has("name")) {
    newErrors[resourceFieldId("name", resource.id)] =
      resourceNameError(resource.name, t) ??
      t(
        "Dashboard.Wizard.Steps.validation.resourceNameTooLong",
        `Name can be at most ${RESOURCE_NAME_MAX} characters`,
        { max: RESOURCE_NAME_MAX },
      );
  }
  if (flagged.has("email")) {
    newErrors[resourceFieldId("email", resource.id)] = resourceEmailError(t);
  }

  return Object.keys(newErrors).length > 0 ? newErrors : null;
};

/** The DOM id of one service's input, and the key its error is stored under. */
export const serviceFieldId = (field, serviceId) =>
  `service-${field}-${serviceId}`;

// ServiceRequest's limits: a name of 1–200 characters, and a duration of at
// least a minute and at most a day. The wizard's own floor is five minutes —
// nobody books a one-minute appointment, and a typo of "3" for "30" is likelier.
const SERVICE_NAME_MAX = 200;
export const SERVICE_DURATION_MIN = 5;
export const SERVICE_DURATION_MAX = 1440;

export const validateServices = (services, t = identityT) => {
  const newErrors = {};

  (services ?? []).forEach((service) => {
    const name = service.name ?? "";
    if (!name.trim()) {
      newErrors[serviceFieldId("name", service.id)] = t(
        "Dashboard.Wizard.Steps.validation.serviceNameRequired",
        "Service name is required",
      );
    } else if (name.length > SERVICE_NAME_MAX) {
      newErrors[serviceFieldId("name", service.id)] = t(
        "Dashboard.Wizard.Steps.validation.resourceNameTooLong",
        `Name can be at most ${SERVICE_NAME_MAX} characters`,
        { max: SERVICE_NAME_MAX },
      );
    }

    const duration = Number(service.duration);
    if (
      !Number.isInteger(duration) ||
      duration < SERVICE_DURATION_MIN ||
      duration > SERVICE_DURATION_MAX
    ) {
      newErrors[serviceFieldId("duration", service.id)] = t(
        "Dashboard.Wizard.Steps.validation.serviceDurationInvalid",
        `Duration must be between ${SERVICE_DURATION_MIN} and ${SERVICE_DURATION_MAX} minutes`,
        { min: SERVICE_DURATION_MIN, max: SERVICE_DURATION_MAX },
      );
    }
  });

  if (!services || services.length === 0) {
    newErrors.services = t(
      "Dashboard.Wizard.Steps.validation.servicesRequired",
      "At least one service is required",
    );
  }

  return newErrors;
};

export const validateStep = (step, formData, t = identityT) => {
  switch (step) {
    case 2:
      return validateBusinessDetails(formData, t);
    case 3:
      return validateResources(formData.resources, formData.plan, t);
    case 4:
      return validateServices(formData.services, t);
    case 5:
      return {}; // Booking page is optional
    default:
      return {};
  }
};
